import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Routes } from 'routes';
import classNames from 'classnames';

import { ISubLink } from 'components/common/StickyTabsNav/types';
import { TabLink } from 'components/blocks/TabNav/Link';
import { useAnalytics, SubscriberDataEvent } from 'util/analytics';
import Link from 'next/link';
import { IResortPathInfo } from 'api/resort/types';
import { PlayerYouTube } from 'components/home/PlayerYouTube';
import { ReactComponent as SvgD } from 'public/assets/icons/swallowtail-flag.svg';
import { ReactComponent as SvgDSummer } from 'public/assets/icons/swallowtail-flag-summer.svg';

import { IRegionShort, IRelatedForRegion, IRelatedForResort } from 'api/region/types';
import { ReactComponent as SvgPhone } from 'public/assets/icons/phone-simple.svg';
import { ReactComponent as SvgPhoneSummer } from 'public/assets/icons/phone-simple-summer.svg';
import styles from './styles.module.scss';
import summerStyles from './summer-styles.module.scss';
import { GlobalHeroNav } from './GlobalHeroNav';

const PILLS_TO_SHOW = 2;

/**
 * Renders resort/region navigation with background hero image.
 */
export const HeroNav = ({
  links,
  link,
  isPartner,
  website,
  phone,
  resort,
  video,
  title,
  region,
  related,
  backgroundPhoto,
  isOnSummerOverview,
}: {
  title: string,
  backgroundPhoto: string,
  links?: ISubLink<string>[],
  link?: string,
  isPartner?: boolean,
  website?: string,
  phone?: string,
  resort?: IResortPathInfo,
  video?: string,
  region?: IRegionShort,
  related?: IRelatedForRegion | IRelatedForResort,
  isOnSummerOverview?: boolean;
}) => {
  const tagsContainerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [forceColumnLayout, setForceColumnLayout] = useState(false);
  const storedTagsRowWidthRef = useRef<number | null>(null);

  const { t } = useOTSTranslation('common');
  const a = useAnalytics();

  const heroNavStyles = isOnSummerOverview ? summerStyles : styles;

  const onWebsiteClick = useCallback(() => {
    a.subcriberEvent(SubscriberDataEvent.HomePageClick, { url: website });
  }, [a, website]);

  const onPhoneClick = useCallback(() => {
    a.subcriberEvent(SubscriberDataEvent.PhoneClick, { url: `tel:${phone}` });
  }, [a, phone]);

  const isResortPage = related && 'nearby' in related;

  useEffect(() => {
    if (typeof window === 'undefined' || !isResortPage) {
      return undefined;
    }

    const evaluateLayout = () => {
      const container = tagsContainerRef.current;
      if (!container) {
        return;
      }

      const {
        width: currentWidth,
      } = container.getBoundingClientRect();

      if (storedTagsRowWidthRef.current === null || currentWidth > storedTagsRowWidthRef.current) {
        storedTagsRowWidthRef.current = currentWidth;
      }

      const referenceWidth = storedTagsRowWidthRef.current ?? currentWidth;
      const windowWidth = window.innerWidth;
      const shouldForceColumn = referenceWidth > windowWidth;
      setForceColumnLayout(shouldForceColumn);
    };

    evaluateLayout();

    const handleResize = () => {
      evaluateLayout();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isResortPage]);

  if (!resort && !region) {
    return <GlobalHeroNav title={title} backgroundPhoto={backgroundPhoto} />;
  }

  return (
    <div
      className={classNames(heroNavStyles.header)}
      ref={headerRef}
    >
      <div className={classNames(heroNavStyles.content, !video && heroNavStyles.contentNoVideo)}>
        <div className={heroNavStyles.hero}>
          {isPartner && (
            <div className={classNames(heroNavStyles.row, heroNavStyles.partnerContainer, !video && heroNavStyles.noVideo)}>
              {!video && (
                <div className={heroNavStyles.noVideoPremierBadge}>
                  <div>
                    {isOnSummerOverview ? <SvgDSummer width={230} height={32} /> : <SvgD width={230} height={32} />}
                    <p>{isOnSummerOverview ? t('common.summerPartner').toUpperCase() : t('common.premierPartner').toUpperCase()}</p>
                  </div>
                </div>
              )}
              <div className={classNames(heroNavStyles.column, !video && heroNavStyles.noVideoRow)}>
                {website && (
                  <a
                    onClick={onWebsiteClick}
                    href={website}
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    {t('common.website')}
                  </a>
                )}

                {phone && (
                  <a
                    onClick={onPhoneClick}
                    href={`tel:${phone}`}
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    {isOnSummerOverview ? <SvgPhoneSummer /> : <SvgPhone />}
                    {phone}
                  </a>
                )}
              </div>
              {video && (

                <div className={classNames(heroNavStyles.column, heroNavStyles.videoContainer)}>
                  <div className={heroNavStyles.badge}>
                    {isOnSummerOverview ? <SvgDSummer width={230} height={32} /> : <SvgD width={230} height={32} />}
                    <p>{isOnSummerOverview ? t('common.summerPartner').toUpperCase() : t('common.premierPartner').toUpperCase()}</p>
                  </div>
                  <PlayerYouTube
                    urlYouTube={video}
                  />

                </div>
              )}

            </div>
          )}
          {region && resort && (
            <div className={heroNavStyles.topContent}>
              <div className={classNames(heroNavStyles.title)}>
                <h1>{title}</h1>
              </div>
              {isResortPage && (
                <div className={classNames(heroNavStyles.subtitle)}>
                  <h2>
                    <Link href={Routes.regionSnowReport(region)} prefetch={false}>{region.title.toUpperCase()}</Link>
                    {' '}
                    <span className={heroNavStyles.seperator}>/</span>
                    {' '}
                    {resort.title
                      .toUpperCase()}

                  </h2>
                </div>
              )}
              {isResortPage && (
                <div
                  className={heroNavStyles.tagsContainer}
                >
                  <div
                    className={classNames(
                      heroNavStyles.rows,
                      !isPartner && heroNavStyles.rowsNonPartner,
                      forceColumnLayout && heroNavStyles.rowsForceColumn,
                    )}
                    ref={tagsContainerRef}
                  >
                    <div className={heroNavStyles.row}>
                      {related.related?.length && related.related?.length > 0 ? (
                        <p>
                          {t('common.relatedRegions')}
                        </p>
                      ) : null}
                      {related.related?.slice(0, PILLS_TO_SHOW)
                        .map((item) => <Link key={item.uuid} href={Routes.regionSnowReport(item)} className={heroNavStyles.tag}>{item.title}</Link>)}
                    </div>
                    {(related as IRelatedForResort).nearby && (
                      <div className={heroNavStyles.row}>
                        <p>{t('common.nearbyResorts')}</p>
                        {(related as IRelatedForResort)
                          .nearby
                          ?.map((item) => (
                            <Link key={item.uuid} href={Routes.resortSnowReport(item)} className={heroNavStyles.tag}>
                              {item.title_short}
                            </Link>
                          ))
                          .slice(0, PILLS_TO_SHOW)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {links && (
          <div className={classNames(heroNavStyles.links)}>
            {links.map((tI) => (

              <TabLink
                href={tI.href}
                current={tI.uuid === link}
                external={!!tI.isExternal}
                key={tI.uuid}
                isRedesigned
                isOnSummerOverview={isOnSummerOverview}
              >
                {tI.title}
              </TabLink>
            ))}

          </div>
        )}
      </div>
    </div>
  );
};
